<script setup lang="ts">
export type TagTypes =
  | 'exclusive'
  | 'promo'
  | 'new'
  | 'validated'
  | 'awaiting-upload'
  | 'document-rejected'
  | 'pending-review';

type TagPositions =
  | 'bottom-left'
  | 'bottom-right'
  | 'top-left'
  | 'top-right'
  | 'none';

interface ITagProps {
  type: TagTypes;
  position?: TagPositions;
  iconOnly?: boolean;
  iconDimensions?: number;
  iconClass?: string;
  titleClass?: string;
}

const props = withDefaults(defineProps<ITagProps>(), {
  type: 'exclusive',
  position: 'none',
  iconOnly: false,
  iconDimensions: 12,
  iconClass: '',
  titleClass: 'text-xs sm:text-sm',
});

const { getImage } = useBranding();

const determineIcon = computed(() => {
  switch (props?.type) {
    case 'exclusive':
      return getImage('exclusive');

    case 'promo':
      return 'M9.54639 1.33646C9.47757 1.34259 9.40897 1.35675 9.34229 1.37943L5 2.8599V8.15775L9.34277 9.63822C9.60927 9.72897 9.90503 9.68527 10.1343 9.52152C10.363 9.35777 10.5 9.09185 10.5 8.8101V6.55961C10.8661 6.4111 11.125 6.05323 11.125 5.63383C11.125 5.21443 10.8661 4.85656 10.5 4.70805V2.20756C10.5 1.92581 10.3633 1.65988 10.1343 1.49613C9.96234 1.37313 9.75285 1.3181 9.54639 1.33646ZM4.25 3.03666L2.26953 3.189C1.55728 3.24375 1 3.84561 1 4.55961V6.45805C1 7.15305 1.52863 7.74203 2.21338 7.82328L2.4502 9.9556C2.50095 10.4134 2.88691 10.7588 3.34766 10.7588C3.84541 10.7588 4.25 10.3537 4.25 9.856V3.03666Z';

    case 'new':
      return 'M6.82714 1.33984C6.73808 1.33984 6.64876 1.38577 6.60351 1.47852L6.32128 2.05078C6.28528 2.12478 6.21528 2.1755 6.13378 2.1875L5.50292 2.2793C5.29792 2.3093 5.21575 2.56058 5.36425 2.70508L5.8203 3.15039C5.8793 3.20789 5.90657 3.29107 5.89257 3.37207L5.78417 4.00098C5.74867 4.20498 5.96395 4.36017 6.14745 4.26367L6.71191 3.9668C6.78441 3.9283 6.87133 3.9283 6.94433 3.9668L7.50878 4.26367C7.69178 4.36017 7.90609 4.20498 7.87109 4.00098L7.76269 3.37207C7.74869 3.29057 7.77595 3.20789 7.83495 3.15039L8.29101 2.70508C8.43951 2.56058 8.35831 2.3093 8.15331 2.2793L7.52245 2.1875C7.44145 2.1755 7.37048 2.12478 7.33398 2.05078L7.05175 1.47852C7.00575 1.38577 6.9162 1.33984 6.82714 1.33984ZM3.92577 4.45703C3.74365 4.45709 3.56153 4.54214 3.47753 4.71289L2.69042 6.30957L0.928703 6.56543C0.740203 6.59293 0.584383 6.72477 0.525383 6.90527C0.466883 7.08627 0.515359 7.28497 0.651359 7.41797L1.92675 8.66016L1.62597 10.415C1.59347 10.6025 1.67021 10.7923 1.82421 10.9043C1.97921 11.0163 2.18208 11.0314 2.35058 10.9424L3.92577 10.1143L5.50195 10.9424C5.57495 10.9809 5.65487 11 5.73437 11C5.83787 11 5.94179 10.9678 6.02929 10.9043C6.18279 10.7923 6.25953 10.6025 6.22753 10.415L5.92675 8.66016L7.20116 7.41797C7.33766 7.28547 7.38662 7.08627 7.32812 6.90527C7.26862 6.72427 7.11232 6.59293 6.92382 6.56543L5.1621 6.30957L4.37402 4.71289C4.29002 4.54189 4.1079 4.45697 3.92577 4.45703ZM10.0029 4.90137C9.92911 4.90137 9.85585 4.94033 9.81835 5.01758L9.58398 5.49219C9.55398 5.55319 9.49523 5.59547 9.42773 5.60547L8.90331 5.68164C8.73331 5.70664 8.66508 5.91516 8.78808 6.03516L9.16796 6.40527C9.21646 6.45277 9.23806 6.52137 9.22655 6.58887L9.13769 7.11035C9.10869 7.27985 9.28647 7.4091 9.43847 7.3291L9.90722 7.08203C9.96772 7.05003 10.0391 7.05003 10.0996 7.08203L10.5684 7.3291C10.7204 7.4096 10.8981 7.27985 10.8691 7.11035L10.7803 6.58887C10.7688 6.52187 10.7908 6.45277 10.8398 6.40527L11.2187 6.03516C11.3417 5.91516 11.2735 5.70614 11.1035 5.68164L10.5791 5.60547C10.5116 5.59547 10.4538 5.55319 10.4238 5.49219L10.1894 5.01758C10.1512 4.94033 10.0767 4.90137 10.0029 4.90137Z';

    case 'validated':
      return 'M12 2C6.477 2 2 6.477 2 12C2 17.523 6.477 22 12 22C17.523 22 22 17.523 22 12C22 6.477 17.523 2 12 2ZM17.707 9.707L10.707 16.707C10.512 16.902 10.256 17 10 17C9.744 17 9.488 16.902 9.293 16.707L6.293 13.707C5.902 13.316 5.902 12.684 6.293 12.293C6.684 11.902 7.316 11.902 7.707 12.293L10 14.586L16.293 8.293C16.684 7.902 17.316 7.902 17.707 8.293C18.098 8.684 18.098 9.316 17.707 9.707Z';

    case 'awaiting-upload':
      return 'M6 19.9998C5.45 19.9998 4.97917 19.804 4.5875 19.4123C4.19583 19.0206 4 18.5498 4 17.9998V15.9998C4 15.7165 4.09583 15.479 4.2875 15.2873C4.47917 15.0956 4.71667 14.9998 5 14.9998C5.28333 14.9998 5.52083 15.0956 5.7125 15.2873C5.90417 15.479 6 15.7165 6 15.9998V17.9998H18V15.9998C18 15.7165 18.0958 15.479 18.2875 15.2873C18.4792 15.0956 18.7167 14.9998 19 14.9998C19.2833 14.9998 19.5208 15.0956 19.7125 15.2873C19.9042 15.479 20 15.7165 20 15.9998V17.9998C20 18.5498 19.8042 19.0206 19.4125 19.4123C19.0208 19.804 18.55 19.9998 18 19.9998H6ZM11 7.8498L9.125 9.7248C8.925 9.9248 8.6875 10.0206 8.4125 10.0123C8.1375 10.004 7.9 9.8998 7.7 9.6998C7.51667 9.4998 7.42083 9.26647 7.4125 8.9998C7.40417 8.73314 7.5 8.4998 7.7 8.2998L11.3 4.6998C11.4 4.5998 11.5083 4.52897 11.625 4.4873C11.7417 4.44564 11.8667 4.4248 12 4.4248C12.1333 4.4248 12.2583 4.44564 12.375 4.4873C12.4917 4.52897 12.6 4.5998 12.7 4.6998L16.3 8.2998C16.5 8.4998 16.5958 8.73314 16.5875 8.9998C16.5792 9.26647 16.4833 9.4998 16.3 9.6998C16.1 9.8998 15.8625 10.004 15.5875 10.0123C15.3125 10.0206 15.075 9.9248 14.875 9.7248L13 7.8498V14.9998C13 15.2831 12.9042 15.5206 12.7125 15.7123C12.5208 15.904 12.2833 15.9998 12 15.9998C11.7167 15.9998 11.4792 15.904 11.2875 15.7123C11.0958 15.5206 11 15.2831 11 14.9998V7.8498Z';
    case 'document-rejected':
      return 'M12 2C6.47 2 2 6.47 2 12C2 17.53 6.47 22 12 22C17.53 22 22 17.53 22 12C22 6.47 17.53 2 12 2ZM16.707 15.293C17.098 15.684 17.098 16.316 16.707 16.707C16.512 16.902 16.256 17 16 17C15.744 17 15.488 16.902 15.293 16.707L12 13.414L8.707 16.707C8.512 16.902 8.256 17 8 17C7.744 17 7.488 16.902 7.293 16.707C6.902 16.316 6.902 15.684 7.293 15.293L10.586 12L7.293 8.707C6.902 8.316 6.902 7.684 7.293 7.293C7.684 6.902 8.316 6.902 8.707 7.293L12 10.586L15.293 7.293C15.684 6.902 16.316 6.902 16.707 7.293C17.098 7.684 17.098 8.316 16.707 8.707L13.414 12L16.707 15.293Z';
    case 'pending-review':
      return 'M10 1C9.448 1 9 1.448 9 2C9 2.552 9.448 3 10 3H14C14.552 3 15 2.552 15 2C15 1.448 14.552 1 14 1H10ZM18.9941 3.20508C18.7391 3.22595 18.491 3.34564 18.3125 3.55664C17.9555 3.97764 18.0087 4.6088 18.4297 4.9668L19.8535 6.17383C20.2745 6.53083 20.9057 6.47764 21.2637 6.05664C21.6207 5.63564 21.5675 5.00448 21.1465 4.64648L19.7227 3.43945C19.5122 3.26095 19.2491 3.1842 18.9941 3.20508ZM12 4C7.038 4 3 8.038 3 13C3 17.962 7.038 22 12 22C16.962 22 21 17.962 21 13C21 8.038 16.962 4 12 4ZM12 7C12.552 7 13 7.448 13 8V13C13 13.552 12.552 14 12 14C11.448 14 11 13.552 11 13V8C11 7.448 11.448 7 12 7Z';
    default:
      return '';
  }
});

const determineColor = computed(() => {
  switch (props?.type) {
    case 'exclusive':
      return 'bg-primitive-dark-blue-50 text-primitive-dark-blue-700';

    case 'promo':
      return 'bg-primitive-pink-100 text-primitive-pink-900';

    case 'new':
      return 'bg-primitive-gold-500 text-primitive-gold-900';

    case 'validated':
      return 'bg-green-300 text-green-900 fill-green-900';

    case 'awaiting-upload':
      return 'bg-grey-100 text-grey-800 fill-grey-800';

    case 'document-rejected':
      return 'bg-error-100 text-error-500 fill-error-500';

    case 'pending-review':
      return 'bg-warning-100 text-amber-950 fill-amber-950';

    default:
      return 'hidden';
  }
});

const determinePosition = computed(() => {
  switch (props?.position) {
    case 'top-left':
      return 'absolute z-[4] top-0 left-0';

    case 'bottom-left':
      return 'absolute z-[4] bottom-1 left-1';

    case 'bottom-right':
      return 'absolute z-[4] bottom-2 right-0';
    case 'top-right':
      return 'absolute z-[4] top-0 right-1';

    default:
      return props.iconOnly ? 'p-1 sm:pl-1' : '';
  }
});
</script>
<template>
  <div
    v-if="props?.type === 'exclusive'"
    class="w-1/2 max-w-[100px]"
    :class="determinePosition"
  >
    <img
      class="exclusive drop-shadow-[rgba(216,57,255,1)_0_5px_5px]"
      :src="determineIcon"
      alt="exclusive"
      draggable="false"
    />
  </div>
  <div
    v-else
    class="flex-center w-max rounded-md pl-1"
    :class="determineColor + ' ' + determinePosition"
  >
    <svg
      xmlns="http://www.w3.org/2000/svg"
      :width="iconDimensions"
      :height="iconDimensions"
      :viewBox="`0 0 ${iconDimensions} ${iconDimensions}`"
      :class="props.iconClass"
    >
      <path :d="determineIcon" />
    </svg>
    <p
      class="my-0 sm:my-0.5 sm:leading-1 font-bold mr-1 capitalize ml-0.5"
      :class="titleClass + (iconOnly ? ' hidden sm:flex' : '')"
    >
      {{ $t(props?.type) }}
    </p>
  </div>
</template>
<style scoped lang="scss">
*:hover > .exclusive {
  @apply drop-shadow-[rgba(0,104,255,1)_0_5px_5px];
}
::selection {
  background-color: transparent;
}
::-moz-selection {
  background-color: transparent;
}
</style>
